import React from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Drawer,
  ModalClose,
  Sheet,
  Typography,
} from '@mui/joy';
import type { ReactNode } from 'react';
import { getApiErrorMessage } from '../../lib/apiError';

/**
 * The shell every detail drawer shares.
 *
 * Extracted from `pages/admin/OrgDetailDrawer.tsx` rather than cloned: the
 * codebase already carries five hand-rolled detail drawers, and the three new
 * ones would have made eight copies of the same loading, error and layout
 * handling.
 */
export interface DetailDrawerProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  subtitle?: ReactNode;
  /** Shown top-right, beside the close control. */
  actions?: ReactNode;
  isLoading?: boolean;
  /** Anything thrown by the query. The DRF body is unwrapped for display. */
  error?: unknown;
  children?: ReactNode;
  width?: number | string;
}

export const DetailDrawer: React.FC<DetailDrawerProps> = ({
  open,
  onClose,
  title,
  subtitle,
  actions,
  isLoading,
  error,
  children,
  width = 560,
}) => (
  <Drawer
    open={open}
    onClose={onClose}
    anchor="right"
    size="lg"
    slotProps={{ content: { sx: { width: { xs: '100%', sm: width }, maxWidth: '100%' } } }}
  >
    <ModalClose />
    <Box sx={{ p: { xs: 2, sm: 3 }, display: 'grid', gap: 2, overflowY: 'auto' }}>
      <Box sx={{ pr: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flexWrap: 'wrap' }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography level="h4" sx={{ wordBreak: 'break-word' }}>
              {title}
            </Typography>
            {subtitle && (
              <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          {actions}
        </Box>
      </Box>

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {Boolean(error) && !isLoading && (
        <Alert color="danger" variant="soft">
          {getApiErrorMessage(error, 'This could not be loaded.')}
        </Alert>
      )}

      {!isLoading && !error && children}
    </Box>
  </Drawer>
);

/** A labelled number, for the tile row at the top of a drawer. */
export const DetailMetric: React.FC<{ icon?: ReactNode; label: string; value: ReactNode }> = ({
  icon,
  label,
  value,
}) => (
  <Sheet variant="soft" sx={{ p: 1.5, borderRadius: 'sm', flex: '1 1 110px', minWidth: 110 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary', mb: 0.5 }}>
      {icon}
      <Typography level="body-xs">{label}</Typography>
    </Box>
    <Typography level="title-lg">{value}</Typography>
  </Sheet>
);

/** A label/value line, for the detail lists below the tiles. */
export const DetailRow: React.FC<{ label: string; value: ReactNode }> = ({ label, value }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.5 }}>
    <Typography level="body-sm" sx={{ color: 'text.secondary', flexShrink: 0 }}>
      {label}
    </Typography>
    <Typography level="body-sm" sx={{ textAlign: 'right', wordBreak: 'break-word' }}>
      {value ?? '—'}
    </Typography>
  </Box>
);

/** A titled block within a drawer. */
export const DetailSection: React.FC<{ title: string; action?: ReactNode; children: ReactNode }> = ({
  title,
  action,
  children,
}) => (
  <Box sx={{ display: 'grid', gap: 1 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
      <Typography level="title-sm">{title}</Typography>
      {action}
    </Box>
    {children}
  </Box>
);

export default DetailDrawer;
